import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Truck, Layers, Info, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { SIZES, DELIVERY_DELAY, PANEL_MATERIAL, NO_SUPPORT_NOTICE } from "@/lib/poster";

const shopifyUrl = (id) => `${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${id}`;
const fromPrice = Math.min(...SIZES.map((s) => s.price));

const useStoreProducts = () => {
  const [state, setState] = useState({ products: null, error: false });
  useEffect(() => {
    http.get("/shopify/products").then((r) => setState({ products: r.data, error: false })).catch(() => setState({ products: [], error: true }));
  }, []);
  return state;
};

const ProductCard = ({ p }) => (
  <div className="group flex flex-col grad-card border border-[#C2B4A2]/70 rounded-3xl overflow-hidden" data-testid={`product-card-${p.id}`}>
    <Link to={`/modeles/${p.id}`} className="relative block aspect-[3/4] overflow-hidden bg-[#EADFD3]" data-testid={`product-link-${p.id}`}>
      <img src={p.images[0] || "/modeles/lina-islem.jpg"} alt={`Tableau ${p.name}`} loading="lazy" className={`absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-500 group-hover:scale-[1.03] ${p.images[1] ? "group-hover:opacity-0" : ""}`} />
      {p.images[1] && <img src={p.images[1]} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500" />}
    </Link>
    <div className="p-5 flex flex-col gap-2 flex-1">
      <span className="label-xs">{p.category}</span>
      <h3 className="font-display text-2xl">{p.name}</h3>
      <p className="text-sm text-[#6B5F53]">À partir de {p.price_from || fromPrice} €</p>
      <a href={shopifyUrl(p.id)} className="btn-gold text-xs !px-4 !py-2.5 self-start mt-auto" data-testid={`product-compose-${p.id}`}>
        Composer ce modèle <ArrowRight size={14} />
      </a>
    </div>
  </div>
);

const ProductDetail = ({ p }) => {
  const [img, setImg] = useState(0);
  return (
    <div className="grid lg:grid-cols-12 gap-10" data-testid="product-detail">
      <div className="lg:col-span-7 space-y-3">
        <div className="rounded-3xl overflow-hidden bg-[#EADFD3] border border-[#DDD3C6]">
          <img src={p.images[img]} alt={`Tableau ${p.name}`} className="w-full h-auto block" data-testid="product-main-image" />
        </div>
        {p.images.length > 1 && (
          <div className="flex gap-3">
            {p.images.map((src, i) => (
              <button key={src} type="button" onClick={() => setImg(i)} data-testid={`product-thumb-${i}`}
                className={`w-20 h-24 rounded-xl overflow-hidden border-2 ${i === img ? "border-[#7B5338]" : "border-transparent"}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="lg:col-span-5 space-y-6">
        <span className="label-xs">{p.category}</span>
        <h1 className="font-display text-4xl sm:text-5xl text-gradient">{p.name}</h1>
        <p className="text-[#6B5F53] leading-relaxed">{p.description}</p>
        <div className="rounded-2xl bg-white border border-[#DDD3C6] p-5 space-y-2 text-sm">
          {SIZES.map((s) => <div key={s.id} className="flex justify-between"><span>{s.label}</span><strong>{s.price} €</strong></div>)}
        </div>
        <ul className="text-sm text-[#6B5F53] space-y-2">
          <li className="flex gap-2"><Layers size={16} className="text-[#7B5338] shrink-0" /> {PANEL_MATERIAL}</li>
          <li className="flex gap-2"><Truck size={16} className="text-[#7B5338] shrink-0" /> Livraison offerte sous {DELIVERY_DELAY} — France & Belgique</li>
          <li className="flex gap-2"><Info size={16} className="text-[#7B5338] shrink-0" /> {NO_SUPPORT_NOTICE}</li>
        </ul>
        <a href={shopifyUrl(p.id)} className="btn-gold" data-testid="product-detail-compose-button">Composer ce modèle <ArrowRight size={16} /></a>
      </div>
    </div>
  );
};

export default function Models() {
  const { id } = useParams();
  const { products, error } = useStoreProducts();
  const product = id && products?.find((p) => p.id === id);
  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 py-14 space-y-10">
      {product ? (
        <>
          <Link to="/modeles" className="inline-flex items-center gap-2 text-sm text-[#6B5F53] hover:text-[#7B5338]" data-testid="product-back-link"><ArrowLeft size={14} /> Tous nos modèles</Link>
          <ProductDetail p={product} />
        </>
      ) : (
        <>
          <div className="text-center">
            <span className="label-xs">Nos réalisations</span>
            <h1 className="font-display text-4xl sm:text-5xl mt-3 uppercase tracking-[0.1em] text-gradient">Nos modèles</h1>
            <p className="text-[#6B5F53] mt-4 max-w-xl mx-auto">Choisissez un modèle et personnalisez-le avec vos prénoms, votre date et vos photos.</p>
          </div>
          {!products && <p className="flex justify-center text-[#9A8C7D]" data-testid="products-loading"><Loader2 className="animate-spin" /></p>}
          {error && <p className="text-center text-[#6B5F53]" data-testid="products-error">Nos modèles sont momentanément indisponibles. Réessayez dans quelques instants.</p>}
          {products?.length === 0 && !error && <p className="text-center text-[#6B5F53]" data-testid="products-empty">De nouveaux modèles arrivent bientôt.</p>}
          {products?.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8" data-testid="products-grid">
              {products.map((p) => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
